import { graphQuery, graphMutation } from '@services/commService';
import { ACTIVITY_TYPES, TYPES } from '@shared/Resources/types';
import { updateUserBusinessUnits } from '@containers/Admin/shared/businessUnitService';

export const getUserInformation = async (id) => {
  const query = `
    {
      userData(id: "${id}") {
        id
        roles
        firstName
        fullName
        lastName
        avatar
        email
        phone
        mfaStatus
        isMigratedToCognito
        userName
        createdAt
        birthdate
        state
        experimentalFeatures
        status
        lastPracticed
        country
        countryState
        locale
        businessUnits
        businessUnitsInfo {
          id
          name
        }
        mentor {
          id
          fullName
        }
        course {
          name
          id
        }
      }
    }
  `;
  const res = await graphQuery(query, true);
  return res.data;
};

export const getAllMentors = async () => {
  const query = `
  {
    allMentors(offset: 0, limit: 0){
      id
      fullName
    }
  }
  `;
  const res = await graphQuery(query, true);
  return res.data;
};

export const getUserActivityLog = async (userId, options = {}) => {
  const filters = options.filters || {};
  const sort = options.sort || {};
  const { type, status } = filters;
  const { column: sortBy, direction: sortOrder } = sort;
  const offset = options.offset || 0;
  const limit = options.pageSize;
  const typeFilterExists = (Array.isArray(type) && type.length > 0) || (typeof type === 'string' && type.trim() !== '');

  const queryArguments = [
    `userId: "${userId}"`,
    typeFilterExists && `type: [${type}]`,
    status && `status: "${status}"`,
    sortBy && `sortBy: "${sortBy}"`,
    sortOrder && `sortOrder: ${sortOrder}`,
    limit && `range: [${offset}, ${limit}]`,
  ].filter(Boolean).join(', ');

  const query = `
  {
    activityLogs(${queryArguments}) {
      id
      type
      actionType
      initiator {
        id
        fullName
        roles
      }
      details
      startDate
      duration
      status
      activityId
      externalId
    }
  }
  `;
  const res = await graphQuery(query, true, {}, options.signal);

  return res.data.activityLogs ?? [];
};

export const getUserPendingPracticeDetails = async (userId, activityId) => {
  const entity = `practice {
    id
    name
  }
  charts  {
    type
    subType
    exercises
    productionsWithErrors
    productionsWithoutErrors
    errors
  }
  `;
  const query = `
  {
    userActivityCharts(id: "${activityId}", userId: "${userId}" ){
      id
      type
      ${entity}
    }
  }`;

  const res = await graphQuery(query, true);

  return res.data;
};

export async function getUserActivityDetail(activityId, activityType) {
   const supportedTypes = ['PRACTICE', 'ACTIVITY_PRACTICE', 'POLL', 'ACTIVITY_POLL', 'LESSON', 'ACTIVITY_LESSON'];
   if (!supportedTypes.includes(activityType)) {
     throw new Error(`Unsupported activity type: ${activityType}`);
   }
   const extraDetails = ['PRACTICE', 'ACTIVITY_PRACTICE', 'POLL', 'ACTIVITY_POLL'].includes(activityType) ? 'hasFeedback\n isFeedbackDismissed' : '';
    const query = `
    {
      userActivityDetails(id: "${activityId}", type: "${activityType}") {
        name
        date
        status
        passedAt
        course
        skill
        ${extraDetails}
      }
    }
    `;
    const response = await graphQuery(query, true);
    if (response.errors || !response.data.userActivityDetails) {
      throw new Error('No activity details were returned.');
    }
    return response.data.userActivityDetails;
}

export const setUserMentors = async (userId, mentorIds) => {
  const formattedMentorIds = Array.isArray(mentorIds)
    ? mentorIds.map((mentorId) => `"${mentorId}"`).join(', ')
    : '';

  const mutation = `
    mutation {
      setUserMentorRelationships(userId: "${userId}", mentorIds: [${formattedMentorIds}])
    }
  `;

  return await graphMutation(mutation, {}, true);
};

export const sendUserCourse = async (courseId, userId) => {
  const mutation = `
    mutation {
      duplicateCourse(copiedFrom:"${courseId}", owner:"${userId}" ) {
        id
        name
        createdBy
      }
    }
  `;
  const { data } = await graphMutation(mutation, {}, true);
  return data;
};

export const sendUserBusinessUnits = async (userId, businessUnits) => {
  return await updateUserBusinessUnits(userId, businessUnits);
};

export const moveUserToCourseLevel = async ({ userId, activityId, activityType }) => {
  const variables = { userId, activityId, activityType };
  const mutation = `
    mutation($userId: String!, $activityId: String!, $activityType: CourseActivityType!) {
      moveUserToCourseActivity(
        userId: $userId
        activityId: $activityId
        activityType: $activityType
      ) {
        courseId
        skillId
        entityId
        entityType
      }
    }
  `;
  return await graphMutation(mutation, variables, true);
};

export const setUserCourseLocationFlag = async ({ userId, activityId, activityType }) => {
  const variables = { userId, activityId, activityType };
  const mutation = `
    mutation($userId: String!, $activityId: String!, $activityType: CourseActivityType!) {
      setUserCourseLocationFlag(
        userId: $userId
        activityId: $activityId
        activityType: $activityType
      ) {
        courseId
        skillId
        entityId
        entityType
      }
    }
  `;
  return await graphMutation(mutation, variables, true);
};

export const clearUserCourseLocationFlag = async (userId) => {
  const mutation = `
    mutation($userId: String!) {
      clearUserCourseLocationFlag(userId: $userId)
    }
  `;
  return await graphMutation(mutation, { userId }, true);
};

export const sendUserStatus = async (userId, state) => {
  const mutation = `
    mutation {
      createUserState(userId: "${userId}", state: ${state}){
          state
          cognitoUserMissing
      }
    }
  `;
  return await graphMutation(mutation, {}, true);
};

export const sendUserLocale = async (userId, locale) => {
  const mutation = `
    mutation {
      updateUserLocale(userId: "${userId}", locale: ${locale}){
          locale
      }
    }
  `;
  return await graphMutation(mutation, {}, true);
};

export const getUserPersonalProgram = async (userId) => {
  const query = `
  {
    entityCoursesForUser(userId: "${userId}") {
      id
      name
      type
      Skills {
        id
        name
        type
        children {
          id
          type
        }
        Lessons {
          id
          hasPassed
          isAccessible
          name
          type
        }
        Practices {
          id
          hasPassed
          isAccessible
          name
          type
        }
        Polls {
          id
          hasPassed
          isAccessible
          name
          type
        }
      }
    }
    userData(id: ["${userId}"]) {
      nextCourseActivity {
        courseId
        skillId
        entityId
        entityType
      }
      flaggedCourseLocation {
        courseId
        skillId
        entityId
        entityType
      }
    }
  }
  `;
  const res = await graphQuery(query, true);
  return res.data;
};

// Admin reset password (generate temp password via API)
export const adminResetPassword = async (userId) => {
  const mutation = `
    mutation {
      adminForceResetUserPassword(userId: "${userId}") {
        temporaryPassword
        emailSent
        blockedReason
      }
    }
  `;
  try {
    const res = await graphMutation(mutation, {}, true);
    return res?.data?.adminForceResetUserPassword;
  } catch (err) {
    throw new Error(err.message || 'Failed to reset password');
  }
};

export const sendUserExperimentalFeatures = async (userId, experimentalFeatures) => {
  const mutation = `
    mutation {
      updateUserExperimentalFeatures(userId: "${userId}", experimentalFeatures: ${experimentalFeatures}) {
        experimentalFeatures
      }
    }
  `;
  return await graphMutation(mutation, {}, true);
};
